import { useRef, useState, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import { Button } from '@/components/ui/button';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { NotificationBell } from '@/components/NotificationBell';
import { ThemeToggle } from '@/components/ThemeToggle';
import { InstallAppChip } from '@/components/InstallAppChip';
import { TrialBanner } from '@/components/TrialBanner';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import {
  LayoutDashboard,
  FolderKanban,
  ClipboardCheck,
  Zap,
  GitBranch,
  BarChart3,
  MessageSquare,
  Mail,
  Users,
  FileText,
  Settings,
  LogOut,
  Menu,
  X,
  ChevronDown,
  ChevronRight,
  Shield,
  ClipboardList,
  MailPlus,
  Clock,
  Activity,
  FileStack,
  Archive,
  Eye,
  Truck,
  ShieldAlert,
  Workflow,
  Target,
  Columns3,
  Inbox,
  Compass,
  CalendarDays,
  Sparkles,
  Building2,
  Briefcase,
  User as UserIcon
} from 'lucide-react';

const LOGO_URL = "https://customer-assets.emergentagent.com/job_9979a9aa-b963-4578-b070-131e8996775c/artifacts/f5sufog6_426c0771-cdbe-4ae6-9d1a-0340a5656588.jpg";

const navItems = [
  { path: '/dashboard', label: 'Dashboard', icon: LayoutDashboard },
  {
    // Grouped CI-toolkit entry — single sidebar row that flies out a
    // submenu to the right. Each child is gated by its own platform
    // feature key so a tenant can be granted any subset.
    key: 'projects-group',
    label: 'Projects',
    icon: Briefcase,
    children: [
      { path: '/projects', label: 'DMAIC/Kaizen', icon: FolderKanban, alts: ['/kaizen'], module: 'dmaic' },
      { path: '/audits', label: '6S Audits', icon: ClipboardCheck, module: 'audits' },
      { path: '/vsm', label: 'Value Stream/Process Flow', icon: GitBranch, alts: ['/process-flow'], module: 'vsm' },
      { path: '/kpis', label: 'KPIs, Goals & OKRs', icon: BarChart3, alts: ['/goals'], module: 'kpis' },
    ],
  },
  {
    // Workfloor group — day-to-day warehouse operations modules.
    key: 'workfloor-group',
    label: 'Workfloor',
    icon: Workflow,
    children: [
      { path: '/expedition', label: 'Expedition', icon: Truck, module: 'expedition' },
      { path: '/dockplanning', label: 'Dockplanning', icon: Columns3, module: 'dockplanning' },
      { path: '/picking', label: 'Order Picking', icon: ClipboardList, module: 'picking' },
      { path: '/damage-log', label: 'Damage Log', icon: ShieldAlert, module: 'damage_log' },
      { path: '/checklists', label: 'Checklists', icon: ClipboardList, module: 'checklists' },
    ],
  },
  { path: '/method-os', label: 'KANRI Method', icon: Compass },
  { path: '/teams', label: 'Teams', icon: Users, module: 'teams' },
  { path: '/planning-v2', label: 'FlowCrew\u2122', icon: CalendarDays, module: 'planning' },
  { path: '/archive', label: 'Archive', icon: Archive },
  { path: '/messages', label: 'Messages', icon: Mail, module: 'messages' },
];

// Sidebar entries carry a `module` (leaf feature key). Entries without
// one are always visible (Dashboard, Archive). Group rows are visible
// when at least one child is granted; the flyout shows only the granted
// children. Master Admins + legacy tenants bypass all gating.

const adminItems = [
  { path: '/admin/users', label: 'User Management', icon: Users },
  { path: '/admin/invitations', label: 'Invitations', icon: MailPlus },
  { path: '/admin/pending-changes', label: 'Pending Changes', icon: Clock },
  // Tenant administration (modules + billing-transfer queue). Master
  // Admins only — handles all non-Smeding tenants.
  { path: '/admin/tenants', label: 'Tenants', icon: Building2, masterOnly: true },
  // Activity Log is the central audit trail — visible only to Master
  // Admins because it lists every edit + the delete button can scrub
  // records permanently.
  { path: '/admin/activity-log', label: 'Activity Log', icon: Activity, masterOnly: true },
  { path: '/admin/checklist-templates', label: 'Checklist Templates', icon: FileStack },
  { path: '/admin/export', label: 'Export Data', icon: FileText },
  { path: '/admin-inbox', label: 'Contact Inbox', icon: Inbox },
];

// Sidebar group with side-flyout. When the sidebar is COLLAPSED (72px)
// we open the flyout on hover so the user gets the same grouping
// affordance without having to click — useful on laptop screens where
// vertical sidebar scrolling is annoying. When EXPANDED we keep the
// click-to-toggle pattern; hovering a long list of group rows shouldn't
// pop them open accidentally as the user scans.
const HOVER_CLOSE_DELAY_MS = 140;

const SidebarGroup = ({ item, collapsed, location }) => {
  const [open, setOpen] = useState(false);
  const closeTimer = useRef(null);
  const Icon = item.icon;
  const isActive = item.children.some(c => {
    const all = [c.path, ...(c.alts || [])];
    return all.some(p => location.pathname === p || location.pathname.startsWith(p + '/'));
  });

  const cancelClose = () => {
    if (closeTimer.current) {
      clearTimeout(closeTimer.current);
      closeTimer.current = null;
    }
  };
  const scheduleClose = () => {
    cancelClose();
    closeTimer.current = setTimeout(() => setOpen(false), HOVER_CLOSE_DELAY_MS);
  };

  // Hover-open only applies in collapsed mode. We attach the same
  // enter/leave handlers to the trigger and the popover content so
  // the cursor can travel across the 8px gap without the menu
  // snapping shut.
  const hoverProps = collapsed
    ? {
        onMouseEnter: () => { cancelClose(); setOpen(true); },
        onMouseLeave: scheduleClose,
      }
    : {};

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          data-testid={`nav-group-${(item.key || item.label).toLowerCase()}`}
          className={`sidebar-item w-full ${isActive ? 'active' : ''}`}
          title={collapsed ? item.label : undefined}
          aria-haspopup="menu"
          aria-expanded={open}
          {...hoverProps}
        >
          <Icon className="h-5 w-5 flex-shrink-0" />
          {!collapsed && (
            <>
              <span className="font-body flex-1 text-left">{item.label}</span>
              <ChevronRight className="h-4 w-4 flex-shrink-0 opacity-70" />
            </>
          )}
        </button>
      </PopoverTrigger>
      <PopoverContent
        side="right"
        align="start"
        sideOffset={8}
        className="w-64 p-2 bg-popover border-border"
        data-testid={`nav-group-${(item.key || item.label).toLowerCase()}-flyout`}
        {...hoverProps}
      >
        <div className="px-2 py-1 mb-1 text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">
          {item.label}
        </div>
        <div className="space-y-1">
          {item.children.map(child => {
            const ChildIcon = child.icon;
            const allPaths = [child.path, ...(child.alts || [])];
            const childActive = allPaths.some(p => location.pathname === p || location.pathname.startsWith(p + '/'));
            return (
              <Link
                key={child.path}
                to={child.path}
                onClick={() => setOpen(false)}
                data-testid={`nav-${child.path.replace('/', '')}`}
                className={`sidebar-item ${childActive ? 'active' : ''}`}
              >
                <ChildIcon className="h-4 w-4 flex-shrink-0" />
                <span className="font-body text-sm">{child.label}</span>
              </Link>
            );
          })}
        </div>
      </PopoverContent>
    </Popover>
  );
};

export const Sidebar = ({ collapsed, onToggle }) => {
  const location = useLocation();
  const { user, isAdmin, isGuest, isMasterAdmin, isCompanyAdmin, isLegacyTenant, entitlements, logout } = useAuth();
  const navigate = useNavigate();

  // Platform-module visibility. Master Admins + legacy tenants see all.
  // When the snapshot isn't loaded yet (or has no list) we show all.
  const moduleAllowed = (mod) => {
    if (!mod || isMasterAdmin || entitlements?.is_legacy) return true;
    const pm = entitlements?.platform_modules;
    return !Array.isArray(pm) || pm.includes(mod);
  };
  const visibleNavItems = navItems
    .map((item) => {
      if (item.children) {
        const kids = item.children.filter((c) => moduleAllowed(c.module));
        return kids.length ? { ...item, children: kids } : null;
      }
      return moduleAllowed(item.module) ? item : null;
    })
    .filter(Boolean);

  // Unread chat-message count — drives the red dot on the Messages
  // nav row. Cheap polling (30s) keeps the implementation trivial;
  // the live UX on /messages itself uses WebSocket so users in the
  // app feel real-time even without this loop firing.
  const [unreadMessages, setUnreadMessages] = useState(0);
  useEffect(() => {
    if (isGuest || !user) return;
    let cancelled = false;
    const token = localStorage.getItem('auth_token') || sessionStorage.getItem('auth_token');
    if (!token) return;
    const API = process.env.REACT_APP_BACKEND_URL;
    const tick = () => {
      fetch(`${API}/api/chat/unread-count`, { headers: { Authorization: `Bearer ${token}` } })
        .then(r => (r.ok ? r.json() : { count: 0 }))
        .then(d => { if (!cancelled) setUnreadMessages(Number(d?.count || 0)); })
        .catch(() => {});
    };
    tick();
    const id = setInterval(tick, 30000);
    // Refresh when the user lands on /messages — clears the dot the
    // instant they read the thread, instead of waiting for the next poll.
    const onFocus = () => tick();
    window.addEventListener('focus', onFocus);
    return () => { cancelled = true; clearInterval(id); window.removeEventListener('focus', onFocus); };
  }, [isGuest, user, location.pathname]);

  const handleLogout = async () => {
    await logout();
    navigate('/');
  };

  return (
    <aside className={`fixed left-0 top-0 h-full bg-gradient-to-b from-secondary/60 to-card border-r border-border z-40 transition-all duration-300 flex flex-col ${collapsed ? 'w-[72px] sidebar-collapsed' : 'w-[280px]'}`}>
      {/* Logo + collapse toggle. When the sidebar is collapsed we center the
          toggle button inside a tighter row so the menu icon stays fully
          visible (a 40px icon button + 16px side padding = 72px exactly,
          which used to clip against the right edge). */}
      <div className={`h-16 flex items-center border-b border-border flex-shrink-0 ${collapsed ? 'justify-center px-0' : 'justify-between px-4'}`}>
        {!collapsed && (
          <Link to="/dashboard" className="flex items-center gap-2.5 min-w-0">
            <span className="relative inline-flex items-start shrink-0">
              <img src="/brand/kanriva-logo.png" alt="Kanriva™" className="h-6 w-auto" />
            </span>
            <span className="text-[9px] uppercase tracking-wider text-muted-foreground border-l border-border pl-2.5 leading-tight">powered by<br />Kaizen Kanri</span>
          </Link>
        )}
        <Button variant="ghost" size="icon" onClick={onToggle} data-testid="sidebar-toggle">
          {collapsed ? <Menu className="h-5 w-5" /> : <X className="h-5 w-5" />}
        </Button>
      </div>

      {/* Navigation - Scrollable */}
      <nav className="flex-1 overflow-y-auto p-2 space-y-1 scrollbar-thin">
        {visibleNavItems.map((item) => {
          // Group items render via SidebarGroup (Popover + hover-open
          // when collapsed, click-toggle when expanded).
          if (item.children) {
            return (
              <SidebarGroup
                key={item.key || item.label}
                item={item}
                collapsed={collapsed}
                location={location}
              />
            );
          }

          const Icon = item.icon;
          const allPaths = [item.path, ...(item.alts || [])];
          const isActive = allPaths.some(p => location.pathname === p || location.pathname.startsWith(p + '/'));
          const showUnreadDot = item.path === '/messages' && unreadMessages > 0;

          return (
            <Link
              key={item.path}
              to={item.path}
              data-testid={`nav-${item.path.replace('/', '')}`}
              className={`sidebar-item ${isActive ? 'active' : ''}`}
              title={collapsed ? item.label : undefined}
            >
              <span className="relative flex-shrink-0">
                <Icon className="h-5 w-5" />
                {showUnreadDot && (
                  <span
                    className="absolute -top-1 -right-1 h-2.5 w-2.5 rounded-full bg-rose-500 ring-2 ring-white animate-pulse"
                    data-testid="messages-unread-dot"
                    aria-label={`${unreadMessages} unread message${unreadMessages === 1 ? '' : 's'}`}
                  />
                )}
              </span>
              {!collapsed && (
                <span className="font-body flex-1 flex items-center justify-between">
                  <span>{item.label}</span>
                  {showUnreadDot && (
                    <span
                      className="ml-2 inline-flex items-center justify-center min-w-[20px] h-5 px-1.5 rounded-full bg-rose-500 text-white text-[10px] font-bold tabular-nums"
                      data-testid="messages-unread-count"
                    >
                      {unreadMessages > 99 ? '99+' : unreadMessages}
                    </span>
                  )}
                </span>
              )}
            </Link>
          );
        })}

        {isMasterAdmin && !isGuest && (
          <Link
            to="/company-admin"
            data-testid="nav-company-admin"
            className={`group flex items-center gap-3 rounded-lg transition-colors ${collapsed ? 'justify-center px-2 py-2' : 'px-4 py-2'} ${location.pathname === '/company-admin' ? 'bg-[#00CED1]/12 text-[#077F87] font-semibold' : 'text-foreground hover:bg-muted'}`}
            title="Company admin"
          >
            <Building2 className="h-4 w-4 flex-shrink-0" />
            {!collapsed && <span className="text-sm">Company admin</span>}
          </Link>
        )}

        {/* Master-Admin only section. Company admins of new tenants
            see the dedicated "Company admin" link above instead — the
            sidebar items here (User Management, Invitations, Pending
            Changes, Activity Log, Tenants, Inbox) are global/legacy
            admin tools meant for the platform owner. */}
        {isMasterAdmin && !isGuest && (
          <>
            <div className={`my-4 ${collapsed ? 'mx-2' : 'mx-4'}`}>
              <div className="zen-line" />
            </div>
            {!collapsed && (
              <div className="px-4 py-2">
                <span className="text-xs font-semibold text-muted-foreground uppercase tracking-wider flex items-center gap-2">
                  <Shield className="h-3 w-3" /> Admin
                </span>
              </div>
            )}
            {adminItems
              .filter(item => !item.masterOnly || isMasterAdmin)
              .map((item) => {
              const Icon = item.icon;
              const isActive = location.pathname === item.path;
              
              return (
                <Link
                  key={item.path}
                  to={item.path}
                  data-testid={`nav-${item.path.replace('/admin/', 'admin-')}`}
                  className={`sidebar-item ${isActive ? 'active' : ''}`}
                  title={collapsed ? item.label : undefined}
                >
                  <Icon className="h-5 w-5 flex-shrink-0" />
                  {!collapsed && <span className="font-body">{item.label}</span>}
                </Link>
              );
            })}
          </>
        )}
      </nav>

      {/* User Profile - Fixed at bottom */}
      <div className="flex-shrink-0 p-4 border-t border-border">
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <button 
              className={`w-full flex items-center gap-3 p-2 rounded-sm hover:bg-muted transition-colors ${collapsed ? 'justify-center' : ''}`}
              data-testid="user-menu-trigger"
            >
              <Avatar className="h-9 w-9">
                <AvatarImage src={user?.picture} />
                <AvatarFallback className="bg-primary text-primary-foreground text-sm">
                  {user?.name?.charAt(0)?.toUpperCase()}
                </AvatarFallback>
              </Avatar>
              {!collapsed && (
                <>
                  <div className="flex-1 text-left">
                    <p className="text-sm font-medium truncate">{user?.name}</p>
                    <p className="text-xs text-muted-foreground capitalize">{user?.role}</p>
                  </div>
                  <ChevronDown className="h-4 w-4 text-muted-foreground" />
                </>
              )}
            </button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="w-56">
            <div className="px-2 py-1.5">
              <p className="text-sm font-medium">{user?.name}</p>
              <p className="text-xs text-muted-foreground">{user?.email}</p>
            </div>
            <DropdownMenuSeparator />
            <DropdownMenuItem asChild>
              <Link to="/profile" className="flex items-center gap-2 cursor-pointer" data-testid="profile-link">
                <UserIcon className="h-4 w-4" /> My Profile
              </Link>
            </DropdownMenuItem>
            <DropdownMenuItem asChild>
              <Link to="/settings" className="flex items-center gap-2 cursor-pointer">
                <Settings className="h-4 w-4" /> Settings
              </Link>
            </DropdownMenuItem>
            <DropdownMenuSeparator />
            <DropdownMenuItem 
              onClick={handleLogout}
              className="text-destructive focus:text-destructive cursor-pointer"
              data-testid="logout-btn"
            >
              <LogOut className="h-4 w-4 mr-2" /> Log out
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
    </aside>
  );
};

export const DashboardLayout = ({ children }) => {
  const [collapsed, setCollapsed] = useState(false);
  const { isGuest } = useAuth();

  return (
    <div className="min-h-screen bg-background">
      <Sidebar collapsed={collapsed} onToggle={() => setCollapsed(!collapsed)} />
      <main className={`transition-all duration-300 ${collapsed ? 'ml-[72px]' : 'ml-[280px]'}`}>
        <TrialBanner />
        {/* Top bar with theme switch + notifications */}
        <div className="flex justify-end items-center gap-1 p-4 kv-topbar" data-testid="app-topbar">
          {isGuest && (
            <div className="flex-1 flex items-center gap-2 px-3 py-1.5 bg-amber-50 border border-amber-200 rounded-md mr-4" data-testid="guest-banner">
              <Eye className="h-4 w-4 text-amber-600 flex-shrink-0" />
              <span className="text-sm text-amber-700 font-medium">Guest Mode</span>
              <span className="text-xs text-amber-600">View only — no changes allowed</span>
            </div>
          )}
          <ThemeToggle />
          <NotificationBell />
        </div>
        <div className="p-6 lg:p-8">
          {children}
        </div>
      </main>
    </div>
  );
};

export default DashboardLayout;
